import { Box, Image } from '@chakra-ui/react';
import { FC } from 'react';

import { useSeasonalAvatarOverlay } from './use-seasonal-avatar-overlay';

interface SeasonalAvatarOverlayProps {
	/** Optional name passed through for `data-testid` continuity with the legacy implementation. */
	name?: string;
}

/**
 * Renders the active season's avatar overlay (e.g. spring wreath, winter
 * wreath) on top of an `<Avatar>`.
 *
 * Positioning is purely percentage-based so the overlay stays
 * pixel-perfect aligned at every avatar size. The wrapper fills the
 * avatar's bounding box via `inset: 0`, the artwork is sized as a
 * fraction of that wrapper (`width: scale * 100%`), and a CSS transform
 * lines the artwork's anchor point up with the wrapper's center.
 *
 * Concretely, with `width: 140%` and `transform: translate(-50%, -50%)`
 * applied at `left: 50%; top: 50%`, the artwork's center sits exactly on
 * the avatar's center. Swapping the translate to e.g. `(-50%, -44%)`
 * shifts the artwork up so its 44%-down anchor — the spring wreath's ring
 * center — lands on the avatar's center instead.
 *
 * This component intentionally has no awareness of avatar pixel sizes:
 * percentage layout means it works for the standard sizes (sm…2xl),
 * custom `boxSize` values, and any responsive size in between.
 */
export const SeasonalAvatarOverlay: FC<SeasonalAvatarOverlayProps> = ({ name }) => {
	const { isEnabled, image, scale, anchor } = useSeasonalAvatarOverlay();

	if (!isEnabled || !image) return null;

	return (
		<Box position="absolute" inset={0} pointerEvents="none" zIndex={1} data-testid={`avatar-overlay-${name}`}>
			<Image
				src={image}
				position="absolute"
				left="50%"
				top="50%"
				width={`${scale * 100}%`}
				height="auto"
				maxWidth="none"
				transform={`translate(${-anchor.x * 100}%, ${-anchor.y * 100}%)`}
				draggable={false}
				aria-hidden="true"
				alt=""
			/>
		</Box>
	);
};
